import { ElMessage, ElMessageBox } from "element-plus";
import type { Router } from "vue-router";

export function isRouteAssetLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (?:CSS )?chunk .+ failed/i.test(
    message,
  );
}

export function installNavigationErrorHandling(
  router: Router,
  reload: (href: string) => void = (href) => window.location.assign(href),
) {
  let recoveryPending = false;
  const failedAssetPaths = new Set<string>();

  function offerRecovery(to: { fullPath: string }) {
    if (recoveryPending) return;
    recoveryPending = true;
    const currentPath = router.currentRoute.value.fullPath;
    const targetHref = router.resolve(to.fullPath).href;

    // A full reload also retries imports that the browser has cached as failed.
    // Ask first so an unsaved form on the current page is not lost silently.
    void ElMessageBox.confirm(
      "页面资源加载失败，可能是网络暂时中断或页面版本已更新。刷新后将重新打开目标页面，当前未保存的内容会丢失。",
      "页面加载失败",
      {
        type: "warning",
        confirmButtonText: "刷新并重试",
        cancelButtonText: "留在当前页",
        closeOnClickModal: false,
      },
    )
      .then(() => {
        // Another successful navigation makes this recovery request obsolete.
        if (router.currentRoute.value.fullPath === currentPath)
          reload(targetHref);
      })
      .catch(() => undefined)
      .finally(() => {
        recoveryPending = false;
      });
  }

  const removeGuard = router.beforeEach((to) => {
    if (!failedAssetPaths.has(to.path)) return true;
    // Vite remembers failed CSS preloads and skips them on another navigation.
    // Re-entering that route without a full reload could render it unstyled.
    offerRecovery(to);
    return false;
  });
  const removeErrorHandler = router.onError((error, to) => {
    if (isRouteAssetLoadError(error)) {
      failedAssetPaths.add(to.path);
      offerRecovery(to);
    } else {
      console.error("页面导航失败", error);
      ElMessage.error("页面跳转失败，请稍后重试。");
    }
  });

  return () => {
    removeGuard();
    removeErrorHandler();
  };
}
